import {
  Canvas,
  FabricImage,
  FabricObject,
  Path,
  Polyline,
  Rect,
  Textbox,
} from 'fabric';
import {
  addTextListeners,
  customiseControlPoints,
  customiseControlStyles,
  customiseLineControlPoints,
  customiseRectControlPoints,
  customiseShapeControlPoints,
  customiseSquareControlPoints,
  initialiseDeleteControl,
  initialiseShapeCursor,
} from './draw-controls';
import {
  BASE_TEXT_STYLE,
  FabricData,
  DRAW_OBJECT_TYPE_PATH,
  OBJECT_SOURCE_KEY,
  OBJECT_SOURCE_KEY_QUESTION,
  BASE_LOCKED_STYLE,
  OBJECT_ARTBOARD_KEY,
} from '../draw-canvas.constants';
import { addBackgroundImage } from './draw-objects';
import { fitContentToCanvas } from '../draw.utils';
import { isSquare } from './is-square';
import { isTriangle } from './is-triangle';
import { isCircle } from './is-circle';
import { isTextbox } from './is-textbox';
import { isRectangle } from './is-rectangle';
import { isLine } from './is-line';
import { LanguageCodes } from 'global/constants';
import { shouldScaleTextForTranslation } from './draw-language.utils';
import { scaleTextToFit } from './draw-text-scaling';

function initialiseImage(img: FabricImage) {
  img.lockRotation = true;
  img.lockScalingFlip = true;
  img.lockMovementY = true;
  img.lockMovementX = true;
  img.lockSkewingX = true;
  img.lockSkewingY = true;
  img.hoverCursor = 'default';
  img.selectable = false;
}

/**
 * Initializes a textbox on the canvas with proper styling and scaling.
 *
 * For non-English translations of creator content (question objects):
 * - Automatically scales down font size to fit within original textbox bounds
 * - Preserves original height while allowing text to wrap naturally
 * - Left-aligns text horizontally and centers vertically within bounds
 *
 * @param canvas - The Fabric.js canvas instance
 * @param text - The textbox to initialize
 * @param isLocked - Whether this is creator content (locked) or student content (editable)
 * @param language - The current display language (undefined = English)
 * @param originalHeight - Original textbox height from saved data
 * @returns Promise that resolves after text scaling is complete
 */
async function initialiseText(
  canvas: Canvas,
  text: Textbox,
  isLocked: boolean = false,
  language: LanguageCodes,
  originalHeight?: number
): Promise<void> {
  if (isLocked) {
    text.set({
      ...BASE_TEXT_STYLE,
      ...BASE_LOCKED_STYLE,
      [OBJECT_SOURCE_KEY]: OBJECT_SOURCE_KEY_QUESTION,
    });

    // Scale text down for non-English languages to prevent wrapping beyond original bounds
    if (shouldScaleTextForTranslation(language)) {
      if (originalHeight && text.fontSize) {
        // Force textbox to recalculate dimensions
        text.initDimensions();

        // Calculate current text height (including all wrapped lines)
        const currentHeight = text.calcTextHeight();

        // If translated text is taller than original, scale down the font
        if (currentHeight > originalHeight) {
          await scaleTextToFit(canvas, text, originalHeight);
        }
      }
    }
  } else {
    text.set({ ...BASE_TEXT_STYLE, selectable: true, editable: true });
    customiseControlPoints(text);
    addTextListeners(canvas, text);
  }
}

/**
 * Reviver function called by Fabric.js when loading objects from JSON.
 * Applies appropriate initialization, controls, and styling to each object based on its type.
 *
 * @param canvas - The Fabric.js canvas instance
 * @param serializedObject - The serialized object data from JSON
 * @param instance - The instantiated Fabric.js object
 * @param isCreateMode - Whether in creation/edit mode (true) or view/present mode (false)
 * @param language - The current display language for text scaling in translations
 * @param pendingPromises - Array to collect async initialization promises
 */
export function reviver<T>({
  canvas,
  serializedObject,
  instance,
  isCreateMode,
  language,
  pendingPromises,
}: {
  canvas: Canvas;
  serializedObject: Record<string, any>;
  instance: T;
  isCreateMode: boolean;
  language: LanguageCodes;
  pendingPromises: Promise<void>[];
}) {
  // Check if this is a question template object that should be locked when we are not in create mode
  const isQuestionObject =
    !isCreateMode &&
    serializedObject[OBJECT_SOURCE_KEY] === OBJECT_SOURCE_KEY_QUESTION;

  const isArtboard = serializedObject[OBJECT_ARTBOARD_KEY];

  if (serializedObject.type.toLowerCase() === 'image') {
    initialiseImage(instance as FabricImage);
  }

  if (isTextbox(instance as FabricObject)) {
    const textPromise = initialiseText(
      canvas,
      instance as Textbox,
      isQuestionObject,
      language,
      serializedObject.height
    );
    pendingPromises.push(textPromise);
    initialiseDeleteControl(canvas, instance as Textbox, isQuestionObject);
    initialiseShapeCursor(instance as Textbox, isQuestionObject);
  }

  if (serializedObject.type.toLowerCase() === DRAW_OBJECT_TYPE_PATH) {
    customiseShapeControlPoints(instance as Path, isQuestionObject);
    customiseControlStyles(instance as Path, isQuestionObject);
    initialiseShapeCursor(instance as Path, isQuestionObject);
    initialiseDeleteControl(canvas, instance as Path, isQuestionObject);
  }

  if (
    isCircle(instance as FabricObject) ||
    isTriangle(instance as FabricObject)
  ) {
    customiseShapeControlPoints(instance as FabricObject, isQuestionObject);
    customiseControlStyles(instance as FabricObject, isQuestionObject);
    initialiseDeleteControl(canvas, instance as FabricObject, isQuestionObject);
    initialiseShapeCursor(instance as FabricObject, isQuestionObject);
  }

  if (!isArtboard && isRectangle(instance as FabricObject)) {
    // Square and rectangle have different control points
    if (isSquare(instance as FabricObject)) {
      customiseSquareControlPoints(instance as FabricObject, isQuestionObject);
    } else {
      customiseRectControlPoints(instance as FabricObject, isQuestionObject);
    }
    customiseControlStyles(instance as FabricObject, isQuestionObject);
    initialiseDeleteControl(canvas, instance as FabricObject, isQuestionObject);
    initialiseShapeCursor(instance as FabricObject, isQuestionObject);
  }

  if (isLine(instance as FabricObject)) {
    const polyline = instance as Polyline;

    // for some reason the points property is readonly
    // so creating a new array prevents an error later on in the out-of-the-box createPolyControls code
    polyline.points = [...polyline.points];

    customiseLineControlPoints(polyline, isQuestionObject);
    customiseControlStyles(polyline, isQuestionObject);
    initialiseDeleteControl(canvas, polyline, isQuestionObject);
    initialiseShapeCursor(polyline, isQuestionObject);
  }

  // this handles the artboard not being selectable
  if (isArtboard) {
    (instance as Rect).selectable = false;
  }
}

/**
 * Loads saved drawing objects from JSON data into the canvas.
 * Filters out image objects (which are added separately as background) and applies
 * the reviver function to each object for proper initialization.
 *
 * @param canvas - The Fabric.js canvas instance
 * @param artboard - The artboard/bounds rectangle for the drawing area
 * @param drawingData - The saved drawing data containing objects and version
 * @param isCreateMode - Whether in creation/edit mode (true) or view/present mode (false)
 * @param language - The current display language for text scaling in translations
 * @returns Promise that resolves when all objects are loaded, initialized, and scaled
 */
export async function reviveSavedObjects({
  canvas,
  artboard,
  drawingData,
  isCreateMode,
  language,
}: {
  canvas: Canvas;
  artboard: Rect;
  drawingData: FabricData;
  isCreateMode: boolean;
  language: LanguageCodes;
}): Promise<void> {
  const pendingPromises: Promise<void>[] = [];

  await canvas.loadFromJSON(
    {
      version: drawingData.version,
      objects: [
        artboard,
        // exclude the image if it is in the saved data
        ...drawingData.objects.filter(r => r.type.toLowerCase() !== 'image'),
      ],
    },
    (object, instance) =>
      reviver({
        canvas,
        serializedObject: object,
        instance,
        isCreateMode,
        language,
        pendingPromises,
      })
  );

  // Wait for all text scaling operations to complete before rendering to image
  await Promise.all(pendingPromises);
}

/**
 * Initializes and positions the canvas with optional background image.
 * Calculates the viewport transform to fit content within the available space.
 *
 * @param canvas - The Fabric.js canvas instance
 * @param dimensions - The dimensions of the drawing area (width and height)
 * @param imageUrl - Optional URL for background image
 * @param artboard - The artboard/bounds rectangle for the drawing area
 * @param instructionHeight - Optional height of instruction area to account for in positioning
 * @returns Promise that resolves when canvas is initialized and positioned
 */
export function initialiseAndPositionCanvas(
  canvas: Canvas,
  dimensions: { width: number; height: number },
  imageUrl: string | null | undefined,
  artboard: Rect,
  instructionHeight?: number
) {
  return new Promise(resolve => {
    if (imageUrl) {
      addBackgroundImage(canvas, imageUrl, dimensions, img => {
        canvas.sendObjectToBack(img);
        canvas.bringObjectForward(img);
        fitContentToCanvas(canvas, artboard, instructionHeight);
        resolve(true);
      });
    } else {
      // allow a small window for element to size so measurements are correct
      setTimeout(() => {
        fitContentToCanvas(canvas, artboard, instructionHeight);
        resolve(true);
      }, 100);
    }
  });
}
